// Minimal web i18n for the share SPA. English is the default/source language;
// RU parity lives in the `ru` dict below for users whose browser reports a
// Russian locale (navigator.language). No runtime locale switcher yet — the
// locale is resolved once at module load. Mirrors the desktop convention
// (app/lib/i18n): dotted keys, {var} interpolation, EN fallback on miss.

type Dict = Record<string, string>;

const en: Dict = {
  'loginGate.title': 'Private recording',
  'loginGate.subWithTeam': '«{teamName}» shared this with you',
  'loginGate.subNoTeam': 'Sign in to listen',
  'loginGate.emailPlaceholder': 'your@email.com',
  'loginGate.requestBtn': 'Get code',
  'loginGate.requestError': "Couldn't send the code. Try again.",
  'loginGate.codeSent': 'Code sent to {email}',
  'loginGate.codePlaceholder': '6-digit code',
  'loginGate.verifyBtn': 'Sign in',
  'loginGate.invalidCode': 'Invalid code',
  // library
  'library.title': 'Team library',
  'library.myTeam': 'My team',
  'library.empty': "You're not on any team yet",
  'library.setlistsHeading': 'Setlists',
  'library.nothingPublished': 'Nothing published yet',
  'library.untitled': 'Untitled',
  'library.songRowMulti': '{name} · {n} versions · {date}',
  'library.songRowSingle': '{name} · {date}',
  'library.setlistRowOne': '{name} · {n} song · {date}',
  'library.setlistRowMany': '{name} · {n} songs · {date}',
  'library.loadError': "Couldn't load library",
  // setlist
  'setlist.empty': 'This setlist is empty',
  'setlist.untitled': 'Untitled',
  'setlist.trackRow': '{pos}. {name}',
  'setlist.loadError': "Couldn't load setlist",
  'setlist.notFound': 'This setlist is no longer available',
  // dashboard
  'dashboard.back': '← Back to library',
  'dashboard.joinCode': 'Join code',
  'dashboard.copy': 'Copy',
  'dashboard.copied': 'Copied',
  'dashboard.members': 'Members · {n}',
  'dashboard.roleOwner': 'Owner',
  'dashboard.roleMember': 'Member',
  'dashboard.empty': "You're not in a team yet. Create or join a team in the Band Way desktop app.",
  'dashboard.loadError': "Couldn't load your team.",
  'dashboard.unknownEmail': '(unknown)',
  // admin
  'admin.title': 'Admin',
  'admin.titleTeams': 'Admin — teams',
  'admin.notAuthorized': 'Not authorized. Sign in as an admin.',
  'admin.signIn': 'Sign in',
  'admin.loadError': "Couldn't load — try again.",
  'admin.retry': 'Retry',
  'admin.actionFailed': 'Action failed — try again',
  'admin.storageQuota': 'Storage quota',
  'admin.setQuota': 'Set quota',
  'admin.membersHeading': 'Members',
  'admin.noMembers': 'No members',
  'admin.sharesHeading': 'Shares',
  'admin.noShares': 'No active shares',
  'admin.back': '← Teams',
  'admin.deactivate': 'Deactivate',
  'admin.activate': 'Activate',
  'admin.remove': 'Remove',
  'admin.revoke': 'Revoke',
  'admin.membersCount': '{n} members',
  'admin.teamRow': '{name} · {owner} · {used} / {limit} · {members}',
  'admin.memberRow': '{email} · {role}',
  'admin.memberRowDeactivated': '{email} · {role} · DEACTIVATED',
  'admin.shareRow': '{name} · {size}',
  'common.unknown': 'unknown',
  'common.mb': 'MB',
  // login portal variant
  'loginGate.portalTitle': 'Welcome to Band Way',
  'loginGate.portalSub': "Sign in to your team's library and setlists",
};

const ru: Dict = {
  'loginGate.title': 'Приватная запись',
  'loginGate.subWithTeam': '«{teamName}» поделились с тобой',
  'loginGate.subNoTeam': 'Войди, чтобы слушать',
  'loginGate.emailPlaceholder': 'your@email.com',
  'loginGate.requestBtn': 'Получить код',
  'loginGate.requestError': 'Не удалось отправить код. Попробуй ещё раз.',
  'loginGate.codeSent': 'Код отправлен на {email}',
  'loginGate.codePlaceholder': '6-значный код',
  'loginGate.verifyBtn': 'Войти',
  'loginGate.invalidCode': 'Неверный код',
  // library
  'library.title': 'Библиотека команды',
  'library.myTeam': 'Моя команда',
  'library.empty': 'Ты ещё не в команде',
  'library.setlistsHeading': 'Сет-листы',
  'library.nothingPublished': 'Пока ничего не опубликовано',
  'library.untitled': 'Без названия',
  'library.songRowMulti': '{name} · версий: {n} · {date}',
  'library.songRowSingle': '{name} · {date}',
  'library.setlistRowOne': '{name} · песен: {n} · {date}',
  'library.setlistRowMany': '{name} · песен: {n} · {date}',
  'library.loadError': 'Не удалось загрузить библиотеку',
  // setlist
  'setlist.empty': 'Сет-лист пуст',
  'setlist.untitled': 'Без названия',
  'setlist.trackRow': '{pos}. {name}',
  'setlist.loadError': 'Не удалось загрузить сет-лист',
  'setlist.notFound': 'Этот сет-лист больше недоступен',
  // dashboard
  'dashboard.back': '← Назад в библиотеку',
  'dashboard.joinCode': 'Код приглашения',
  'dashboard.copy': 'Скопировать',
  'dashboard.copied': 'Скопировано',
  'dashboard.members': 'Участники · {n}',
  'dashboard.roleOwner': 'Владелец',
  'dashboard.roleMember': 'Участник',
  'dashboard.empty': 'Ты ещё не в команде. Создай или присоединись к команде в десктоп-приложении Band Way.',
  'dashboard.loadError': 'Не удалось загрузить команду.',
  'dashboard.unknownEmail': '(неизвестно)',
  // admin
  'admin.title': 'Админ',
  'admin.titleTeams': 'Админ — команды',
  'admin.notAuthorized': 'Нет доступа. Войди как администратор.',
  'admin.signIn': 'Войти',
  'admin.loadError': 'Не удалось загрузить — попробуй ещё раз.',
  'admin.retry': 'Повторить',
  'admin.actionFailed': 'Действие не выполнено — попробуй ещё раз',
  'admin.storageQuota': 'Квота хранилища',
  'admin.setQuota': 'Задать квоту',
  'admin.membersHeading': 'Участники',
  'admin.noMembers': 'Нет участников',
  'admin.sharesHeading': 'Шейры',
  'admin.noShares': 'Нет активных шейров',
  'admin.back': '← Команды',
  'admin.deactivate': 'Деактивировать',
  'admin.activate': 'Активировать',
  'admin.remove': 'Удалить',
  'admin.revoke': 'Отозвать',
  'admin.membersCount': 'участников: {n}',
  'admin.teamRow': '{name} · {owner} · {used} / {limit} · {members}',
  'admin.memberRow': '{email} · {role}',
  'admin.memberRowDeactivated': '{email} · {role} · ДЕАКТИВИРОВАН',
  'admin.shareRow': '{name} · {size}',
  'common.unknown': 'неизвестно',
  'common.mb': 'МБ',
  // login portal variant
  'loginGate.portalTitle': 'Добро пожаловать в Band Way',
  'loginGate.portalSub': 'Войди в библиотеку и сет-листы команды',
};

const DICTS: Record<string, Dict> = { en, ru };
const SUPPORTED = ['en', 'ru'] as const;

/** Resolve a supported locale from a BCP-47 tag, defaulting to English. */
export function resolveLocale(tag: string | undefined | null): 'en' | 'ru' {
  const base = (tag || '').toLowerCase().split('-')[0];
  return (SUPPORTED as readonly string[]).includes(base) ? (base as 'en' | 'ru') : 'en';
}

let _locale: 'en' | 'ru' = 'en';
try {
  if (typeof navigator !== 'undefined') _locale = resolveLocale(navigator.language);
} catch {
  _locale = 'en';
}

export function getLocale(): 'en' | 'ru' {
  return _locale;
}

/** Test-only locale override. */
export function setLocale(loc: 'en' | 'ru'): void {
  _locale = loc;
}

function interpolate(tpl: string, vars?: Record<string, string | number>): string {
  if (!vars) return tpl;
  return tpl.replace(/\{(\w+)\}/g, (_m, k) =>
    Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : `{${k}}`,
  );
}

export function t(key: string, vars?: Record<string, string | number>): string {
  // EN is the source: fall back to it on a missing RU key, then to the key.
  const raw = DICTS[_locale][key] ?? en[key];
  if (raw === undefined) return key;
  return interpolate(raw, vars);
}
